import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import LabelInput from 'components/common/forms/LabelInput';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import FormTextarea from 'components/common/forms/FormTextarea';
import SingleBtn from 'components/common/elements/SingleBtn';
import ParticipationAgreement from 'pages/modal/ParticipationAgreement';
import styles from 'styles/components/pages/student/patient/PatientInfoForm.module.scss';
import { ReactComponent as AgreeIcon } from 'assets/icons/agree.svg';
import {
  RegisterPatientDefaultValues,
  PatientInfoDefaultValues,
} from 'constants/patient';
import { ROUTE_PATH } from 'constants/routePath';
import { FormCheckMsgs } from 'types/components/form';
import {
  postPatientDupCheckPhoneNum,
  postPatient,
  getPatientInfo,
  deletePatient,
  putPatient,
} from 'apis/patient';
import { TRegisterPatientInfoParams } from 'types/apis/patient';
import useModal from 'hooks/useModal';
import {
  calculateAge,
  getTodayDate,
  getTodayMonth,
  getTodayYear,
  isDateBeforeToday,
} from 'utils/date';
import { TCustomAxiosError, isTCustomAxiosError } from 'apis/config/axios';

type TParams = {
  id: string;
};

type TProps = {
  setIsLoading: (isLoading: boolean) => void;
};

const PatientInfoForm = ({ setIsLoading }: TProps) => {
  const { openModal, closeModal } = useModal();
  const { id } = useParams<TParams>();
  const patientId = id as string;
  const navigate = useNavigate();

  const isEditPage = !!patientId;

  const [dupCheckPhoneNumMsg, setDupCheckPhoneNumMsg] = useState<string | null>(
    null
  );
  const [agreementImage, setAgreementImage] = useState<File | null>(null);
  const [signImageURL, setSignImageURL] = useState<string | null>(null);

  const defaultFormValues = isEditPage
    ? RegisterPatientDefaultValues
    : undefined;

  const methods = useForm<TRegisterPatientInfoParams>({
    defaultValues: defaultFormValues,
  });

  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
    getValues,
    reset,
  } = methods;

  const [patientNameValue, birthDateValue, phoneNumValue] = useWatch({
    name: ['name', 'birthDate', 'phoneNum'],
    control,
  });

  const { data: getPatientInfoData } = useQuery(
    ['getPatientInfo', patientId],
    () => getPatientInfo(patientId),
    {
      onSuccess: ({ data: patientInfo }) => {
        if (isEditPage && patientInfo) {
          const formValuesFromData: TRegisterPatientInfoParams = {
            name: patientInfo.name.trim(),
            phoneNum: patientInfo.phoneNum,
            birthDate: patientInfo.birthDate,
            sex: patientInfo.sex,
            relation: patientInfo.relation,
            occupation: patientInfo.occupation,
            complaint: patientInfo.complaint,
          };
          reset(formValuesFromData);
        }
      },
      onError: () => {
        openModal({
          title: '대상자 조회 실패',
          text: '대상자 조회에 실패했습니다. 다시 시도해 주세요.',
          onSubmit: () => navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT),
          onClickOutside: () => navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT),
        });
      },
      enabled: isEditPage,
    }
  );

  const patientInfo = getPatientInfoData?.data || PatientInfoDefaultValues;

  const { mutate: postPatientDupCheckPhoneNumMutation } = useMutation(
    postPatientDupCheckPhoneNum,
    {
      onSuccess: (data) => {
        if (data === null) {
          setDupCheckPhoneNumMsg(FormCheckMsgs.InvalidInputValue);
        }

        if (!data.data) {
          setDupCheckPhoneNumMsg(FormCheckMsgs.AvailablePhoneNum);
        } else {
          setDupCheckPhoneNumMsg(FormCheckMsgs.AlreadyExistPhoneNum);
        }
      },
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          if (
            error.code === 409 &&
            error.message === '같은 번호를 가진 대상자가 존재합니다.'
          ) {
            setDupCheckPhoneNumMsg(FormCheckMsgs.AlreadyExistPatientPhoneNum);
          }
        }
      },
    }
  );

  const { mutate: postPatientMutation } = useMutation(postPatient, {
    onSuccess: () => navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT),
    onError: (error) => onError(error as TCustomAxiosError),
  });

  const { mutate: putPatientMutation } = useMutation(
    (formData: FormData) => putPatient(patientId, formData),
    {
      onSuccess: () => navigate(`${ROUTE_PATH.student.PATIENT}/${patientId}`),
      onError: (error) => onError(error as TCustomAxiosError),
    }
  );

  const { mutate: deletePatientMutation } = useMutation(deletePatient, {
    onSuccess: () => navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT),
    onError: () => {
      openModal({
        isConfirm: false,
        title: '대상자 삭제 실패',
        text: '다시 시도해 주세요.',
      });
    },
  });

  const onClickDupCheckPhoneNum = () => {
    if (!phoneNumValue) {
      return;
    }

    if (phoneNumValue.length !== 11 || !phoneNumValue.startsWith('010')) {
      setDupCheckPhoneNumMsg(FormCheckMsgs.InvalidInputValue);
      return;
    }

    if (phoneNumValue === patientInfo.phoneNum) {
      setDupCheckPhoneNumMsg(FormCheckMsgs.AvailablePhoneNum);
    } else {
      postPatientDupCheckPhoneNumMutation({
        phoneNum: phoneNumValue,
      });
    }
  };

  const onOpenModal = (imgURL?: string | null) =>
    openModal({
      element: (
        <ParticipationAgreement
          name={patientNameValue}
          signImageURL={imgURL || signImageURL}
          onSigned={onSigned}
          onAgreeCompleted={onAgreeCompleted}
          onClose={() => setSignImageURL(null)}
        />
      ),
    });

  const onClickAgreement = () => {
    if (!patientNameValue.trim()) {
      openModal({
        text: '이름을 먼저 입력해 주세요.',
      });
      return;
    }
    onOpenModal();
  };

  const onSigned = (imgURL: string | null) => {
    setSignImageURL(imgURL);
    imgURL && onOpenModal(imgURL);
  };

  const onAgreeCompleted = (file: File | null) => {
    setAgreementImage(file);
    closeModal();
  };

  const onClickDelete = () => {
    openModal({
      isConfirm: true,
      title: '대상자 삭제',
      text: '해당 대상자를 삭제하시겠습니까?',
      onSubmit: () => deletePatientMutation(patientId),
    });
  };

  const onClickCancel = () => {
    const isAllEmpty = Object.values(getValues()).every((value) => {
      if (Array.isArray(value)) {
        return value.length === 0;
      } else {
        return value === '' || value === null;
      }
    });

    if (isAllEmpty) {
      navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT);
    } else {
      openModal({
        isConfirm: true,
        title: '대상자 등록 종료',
        text: '해당 페이지에서 나가면 작성 중인 내용이 저장되지 않습니다. 대상자 등록을 종료하시겠습니까?',
        onSubmit: () => navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT),
      });
    }
  };

  const onError = (error: TCustomAxiosError) => {
    setIsLoading(false);

    if (isTCustomAxiosError(error)) {
      openModal({
        title: `대상자 ${patientId ? '수정' : '등록'} 실패`,
        text: error.code === 400 ? error.message : '다시 시도해 주세요.',
      });
    }
  };

  const onValid = (data: TRegisterPatientInfoParams) => {
    const isPhoneNumChanged = data.phoneNum !== patientInfo.phoneNum;

    if (
      phoneNumValue &&
      isPhoneNumChanged &&
      dupCheckPhoneNumMsg !== FormCheckMsgs.AvailablePhoneNum
    ) {
      setDupCheckPhoneNumMsg(FormCheckMsgs.NeedToDuplicatedCheck);
      return;
    }

    openModal({
      isConfirm: true,
      title: `대상자 ${patientId ? '수정' : '등록'}`,
      text: `해당 대상자를 ${patientId ? '수정' : '등록'}하시겠습니까?`,
      onSubmit: () => {
        const formData = new FormData();

        formData.append(
          'request',
          new Blob([JSON.stringify(data)], {
            type: 'application/json',
          })
        );
        if (agreementImage) {
          formData.append('file', agreementImage);
        }

        if (patientId) {
          putPatientMutation(formData);
        } else {
          postPatientMutation(formData);
        }
      },
    });
  };

  const onSubmitError = (error: object) => {
    if ('name' in error) {
      setAgreementImage(null);
      setSignImageURL(null);
    }
  };

  useEffect(() => {
    setDupCheckPhoneNumMsg(null);
  }, [phoneNumValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onValid, onSubmitError)}>
        <div className={styles.label_wrapper}>
          <div className="w100 mt40">
            <LabelInput
              labelText="이름(Patient Name)"
              placeholderText="대상자 이름을 입력해 주세요."
              name="name"
              required={true}
              rules={{
                required: '이름은 필수입니다.',
                minLength: {
                  value: 2,
                  message: '최소 2자 이상이어야 합니다.',
                },
                maxLength: {
                  value: 30,
                  message: '최대 30자까지 입력할 수 있습니다.',
                },
                pattern: {
                  value: /^[a-zA-Z가-힣\s]{2,30}$/,
                  message: FormCheckMsgs.InvalidInputValue,
                },
              }}
              disabled={!!agreementImage}
            />

            <div style={{ height: '22px' }}>
              {formErrors?.name && (
                <p className={styles.error_msg}>{formErrors.name.message}</p>
              )}
            </div>
          </div>

          <div className="w100 mt40">
            <LabelInput
              labelText="연락처(H.P)"
              placeholderText="‘-’를 제외한 연락처를 입력해 주세요."
              name="phoneNum"
              btnText="중복확인"
              checkAvailability={true}
              required={true}
              onBtnClick={onClickDupCheckPhoneNum}
              rules={{
                required: '휴대폰 번호는 필수입니다.',
                pattern: {
                  value: /^010\d{8}$/,
                  message: FormCheckMsgs.InvalidInputValue,
                },
              }}
            />

            {formErrors?.phoneNum && (
              <div style={{ height: '22px' }}>
                <p className={styles.error_msg}>
                  {formErrors.phoneNum.message}
                </p>
              </div>
            )}
            {dupCheckPhoneNumMsg && (
              <p
                className={`error_text ${
                  dupCheckPhoneNumMsg === FormCheckMsgs.AvailablePhoneNum
                    ? 'purple_text'
                    : ''
                }`}
              >
                {dupCheckPhoneNumMsg}
              </p>
            )}
          </div>
        </div>

        <div className={styles.label_wrapper}>
          <div className="w100 mt24">
            <LabelInput
              labelText="생년월일(Age)"
              placeholderText="생년월일을 입력해 주세요. 예) 20040707"
              name="birthDate"
              required={true}
              rules={{
                required: '생년월일은 필수입니다.',
                pattern: {
                  value:
                    /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/,
                  message: FormCheckMsgs.InvalidInputValue,
                },
                validate: {
                  checkBirthDate: (value) => {
                    if (
                      value.length !== 8 ||
                      isDateBeforeToday(value) === false
                    ) {
                      return FormCheckMsgs.InvalidInputValue;
                    }
                  },
                },
              }}
            />

            <div style={{ height: '22px' }}>
              {formErrors?.birthDate && (
                <p className={styles.error_msg}>
                  {formErrors.birthDate.message}
                </p>
              )}
              {!formErrors.birthDate &&
                birthDateValue &&
                birthDateValue.length === 8 && (
                  <p className={styles.sub_msg}>
                    만 {calculateAge(birthDateValue)}세입니다.
                  </p>
                )}
            </div>
          </div>

          <div className="w100 mt24">
            <label className={styles.label}>
              성별(Sex)<span className="font_red"> *</span>
            </label>
            <div className={styles.radio_wrapper}>
              <FormRadioBtn
                name="sex"
                value="남성"
                labelText="남성"
                rules={{ required: '성별 선택은 필수입니다.' }}
              />
              <FormRadioBtn
                name="sex"
                value="여성"
                labelText="여성"
                rules={{ required: '성별 선택은 필수입니다.' }}
              />
            </div>

            <div style={{ height: '22px' }}>
              {formErrors?.sex && (
                <p className={styles.error_msg}>{formErrors.sex.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className={styles.label_wrapper}>
          <div className="w100 mt24">
            <LabelInput
              labelText="학생과의 관계"
              placeholderText="학생과의 관계를 입력해 주세요. 예) 엄마, 언니"
              name="relation"
            />
          </div>

          <div className="w100 mt24">
            <LabelInput
              labelText="직업(Occupation)"
              placeholderText="대상자 직업을 입력해 주세요. 예) 학생"
              name="occupation"
            />
          </div>
        </div>

        <span className={styles.agree_span}>개인 정보 동의</span>

        {!agreementImage && !patientInfo.agreeDate ? (
          <>
            <div className={styles.register_agree_span}>
              <SingleBtn
                type="button"
                btnText="동의하기"
                isBtnActive={true}
                btnStyle={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  height: '40px',
                  padding: '8px 16px',
                }}
                icon={
                  <AgreeIcon
                    width={20}
                    height={20}
                    fill={STYLES_VARIABLES.WHITE}
                  />
                }
                onClick={onClickAgreement}
              />
            </div>
          </>
        ) : (
          <div className={styles.edit_agree_span}>
            <SingleBtn
              type="button"
              btnText="동의완료"
              btnStyle={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                height: '40px',
                padding: '8px 16px',
              }}
              icon={
                <AgreeIcon
                  width={20}
                  height={20}
                  fill={STYLES_VARIABLES.GRAY_07}
                />
              }
              disabled={true}
            />
            <span className="fs14">
              동의일: {getTodayYear()}년 {getTodayMonth()}월 {getTodayDate()}일
            </span>
          </div>
        )}

        <span className={styles.span}>
          주요 호소 증상(Chief Complaint)
          <span className="font_red"> *</span>
        </span>
        <FormTextarea
          name="complaint"
          placeholder="주요 호소 증상을 입력해 주세요."
          textareaStyle={{
            width: '100%',
            height: '120px',
            marginTop: '12px',
            borderRadius: '8px',
            padding: '12px 16px',
          }}
          rule={{
            required: '주요 호소 증상은 필수입니다.',
          }}
        />
        <div style={{ height: '22px' }}>
          {formErrors?.complaint && (
            <p className={styles.error_msg}>
              주요 호소 증상 작성은 필수입니다.
            </p>
          )}
        </div>

        {isEditPage ? (
          <div className={styles.btn_wrapper}>
            <SingleBtn type="button" btnText="삭제" onClick={onClickDelete} />
            <SingleBtn type="submit" btnText="수정" isBtnActive={true} />
          </div>
        ) : (
          <div className={styles.btn_wrapper}>
            <SingleBtn type="button" btnText="취소" onClick={onClickCancel} />
            <SingleBtn type="submit" btnText="등록" isBtnActive={true} />
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default PatientInfoForm;
